import type { ReactNode } from 'react';

import { Box, Button, Stack } from '@mui/material';

import SwatchLegend from './SwatchLegend';

export const DecisionToolbar = ({
  disabled,
  selectedCellCount,
  hasSelection,
  legendLabel,
  legendOmit,
  legendRelabel,
  legendPlace,
  onTake,
  onDeselect,
  extra,
  leadingWidth,
}: {
  disabled: boolean;
  /**
   * Period cells in the selection. Only these can carry a decision, so this
   * is what gates Take Mine and Take Theirs.
   */
  selectedCellCount: number;
  /**
   * Whether any range exists at all, period cells or not.
   *
   * Gating Clear Selection on `selectedCellCount` left it disabled while a
   * range over the dimension columns was plainly on screen and plainly
   * clearable. A dimension range is a legitimate thing to have - it is how you
   * copy a block of row identities out with Ctrl+C - so it has to be
   * clearable even though there is nothing in it to decide.
   */
  hasSelection: boolean;
  /** Accessible name for this section's colour legend. */
  legendLabel?: string;
  /** Swatches to leave out of the legend. */
  legendOmit?: string[];
  /** Replacement legend labels by swatch class name. */
  legendRelabel?: Record<string, string>;
  /** Replacement legend grid positions by swatch class name. */
  legendPlace?: Record<string, { column: number; row: number }>;
  onTake: (choice: 'MINE' | 'THEIRS') => void;
  onDeselect: () => void;
  /** Rendered inline at the left edge of the row, so it adds no height of its own. */
  extra?: ReactNode;
  /** Width of the area `extra` occupies at the left edge; the buttons start after it. */
  leadingWidth?: string;
}) => {
  const takeDisabled = disabled || selectedCellCount === 0;
  const clearDisabled = disabled || !hasSelection;

  return (
    // A grid so the controls can sit on two rows: the period toggle at the left
    // edge on row 1, the buttons on row 2 directly above the grid (starting at
    // the freeze boundary, `leadingWidth` from the left), and the legend
    // spanning both rows at the far right.
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${leadingWidth ?? '0px'} auto minmax(0, 1fr)`,
        gridTemplateRows: 'auto auto',
        alignItems: 'center',
        rowGap: 0.5,
        mb: 0.5,
      }}
    >
      {extra && (
        <Box sx={{ gridColumn: 1, gridRow: 1, whiteSpace: 'nowrap' }}>
          {extra}
        </Box>
      )}

      <Box sx={{ gridColumn: 2, gridRow: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={takeDisabled}
            aria-label={`Take mine for ${selectedCellCount} selected cells`}
            onClick={() => onTake('MINE')}
          >
            Take Mine
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={takeDisabled}
            aria-label={`Take theirs for ${selectedCellCount} selected cells`}
            onClick={() => onTake('THEIRS')}
          >
            Take Theirs
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={clearDisabled}
            aria-label="Clear selected cells"
            onClick={() => onDeselect()}
          >
            Clear Selection
          </Button>
        </Stack>
      </Box>

      {/*
        The legend sits on the toolbar rather than in the footer, and
        therefore appears once per section - next to the cells whose colours
        it explains, instead of once at the bottom of the page for both.
      */}
      <Box
        sx={{ gridColumn: 3, gridRow: '1 / span 2', justifySelf: 'end', pl: 2 }}
      >
        <SwatchLegend
          ariaLabel={legendLabel}
          omit={legendOmit}
          relabel={legendRelabel}
          place={legendPlace}
        />
      </Box>
    </Box>
  );
};

export default DecisionToolbar;
